/**
 * Assign Tenant Dialog Component
 * 
 * Dialog for assigning a user to a tenant.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2, Building2 } from 'lucide-react';
import { useLanguage } from '@/core/LanguageContext';
import { fetchAllTenants } from '../api';
import type { AuthUserTenantMembership } from '../types';

interface AssignTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  email: string;
  currentMemberships: AuthUserTenantMembership[];
  onSave: (data: { userId: string; tenantId: string }) => void;
  isPending: boolean;
}

export function AssignTenantDialog({
  open,
  onOpenChange,
  userId,
  email,
  currentMemberships,
  onSave,
  isPending,
}: AssignTenantDialogProps) {
  const { t } = useLanguage();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['superadmin', 'tenants'],
    queryFn: fetchAllTenants,
    enabled: open,
  });

  // Filter out tenants user is already a member of
  const existingTenantIds = new Set(currentMemberships.map((m) => m.tenant_id));
  const availableTenants = tenants.filter((t) => !existingTenantIds.has(t.id));

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTenantId('');
    }
  }, [open]);

  const handleSave = () => {
    if (!selectedTenantId) return;
    onSave({ userId, tenantId: selectedTenantId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('superadmin.users.assignTenantTitle', 'Assign to Tenant')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'superadmin.users.assignTenantDescription',
              'Assign this user to an organization tenant. They will be added with approved status.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 rounded-lg bg-muted">
            <p className="text-sm font-medium">{email}</p>
          </div>

          {currentMemberships.length > 0 && (
            <div className="space-y-2">
              <Label>{t('superadmin.users.currentMemberships', 'Current Memberships')}</Label>
              <div className="flex flex-wrap gap-2">
                {currentMemberships.map((m) => (
                  <Badge
                    key={m.tenant_id}
                    variant={m.status === 'approved' ? 'default' : 'secondary'}
                  >
                    {m.tenant_name}
                    {m.status !== 'approved' && ` (${m.status})`}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="tenant-select">
              {t('superadmin.users.selectTenant', 'Select Tenant')}
            </Label>
            {tenantsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.loading', 'Loading...')}
              </div>
            ) : availableTenants.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'superadmin.users.noAvailableTenants',
                  'User is already a member of all available tenants.'
                )}
              </p>
            ) : (
              <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('superadmin.users.selectTenantPlaceholder', 'Choose a tenant')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableTenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      <span className="flex items-center gap-2">
                        <span>{tenant.name}</span>
                        <span className="text-muted-foreground">({tenant.code})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="industrial"
            onClick={handleSave}
            disabled={!selectedTenantId || isPending}
          >
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('superadmin.users.assignButton', 'Assign to Tenant')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
